/**
 * TitlePreview Component
 *
 * Reusable component that shows how a title will appear on the Title page.
 * Used in:
 * - Provider submission review (before submitting)
 * - Admin submission review (before approving/publishing)
 * - Admin title editing (preview changes)
 */

import { Button } from "@lumi/ui-headless";
import { Info, Play, ThumbsUp, Video } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { SPACING } from "../../../../shared/constants/spacing";
import { normalizeCountryName } from "../../../../utils/countryMapping";
import { normalizeLanguageNames } from "../../../../utils/languageMapping";
import { SubmissionImage } from "../../components/SubmissionImage";

export interface TitlePreviewData {
  title: string;
  backdropUrl?: string;
  backdropUrlHd?: string;
  heroUrl?: string;
  logoUrl?: string;
  posterUrl?: string;
  posterUrlHd?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  year?: number;
  resolution?: string;
  languages?: string[];
  country?: string;
  runtime?: number; // in minutes
  genres?: string[];
  director?: string;
  synopsis?: string;
  rating?: string;
}

interface TitlePreviewProps {
  data: TitlePreviewData;
  /** Show as compact preview (hero only) or full preview (with cast/crew sections) */
  variant?: "compact" | "full";
  /** Preview aspect ratio - defaults to 16:9 desktop view */
  aspectRatio?: "16:9" | "9:16"; // Future: mobile preview
  /** Optional submission ID for R2 image loading */
  submissionId?: string | null;
  /** Optional title ID for R2 image loading (when editing titles) */
  titleId?: string | null;
}

/**
 * PreviewHero - Hero section for title preview
 * Matches the visual design of TitleHero component from Title.tsx
 */
/* eslint-disable max-lines-per-function */
// NOTE: Complex hero component with multiple conditional sections
function PreviewHero({
  data,
  submissionId,
  titleId,
}: Readonly<{
  data: TitlePreviewData;
  submissionId?: string | null;
  titleId?: string | null;
}>): ReactElement {
  // Check if backdrop is an R2 key
  const backdropUrl = data.backdropUrlHd || data.heroUrl || data.backdropUrl;
  const isBackdropR2 = backdropUrl?.startsWith("r2://");
  const isLogoR2 = data.logoUrl?.startsWith("r2://");

  // Normalize language names for display
  const languageNames = data.languages ? normalizeLanguageNames(data.languages) : [];

  return (
    <section className="relative h-full flex items-end pointer-events-none">
      {/* Backdrop Image */}
      {backdropUrl && isBackdropR2 && (submissionId || titleId) && (
        <SubmissionImage
          src={backdropUrl}
          submissionId={submissionId}
          titleId={titleId}
          alt={`${data.title} backdrop`}
          className="absolute inset-0 object-cover"
        />
      )}
      {backdropUrl && (!isBackdropR2 || (!submissionId && !titleId)) && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${backdropUrl})` }}
        />
      )}
      {!backdropUrl && <div className="absolute inset-0 bg-neutral-900" />}

      {/* Gradients - matches TitleHero structure */}
      <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-black/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[300px] bg-gradient-to-t from-black via-black/35 to-transparent z-5" />

      {/* Title Info - matches TitleHero spacing and typography */}
      <div className="relative z-10 px-16 pb-8 pointer-events-auto">
        <div className="max-w-2xl space-y-0">
          {/* Title - Logo if available, otherwise text */}
          {data.logoUrl && isLogoR2 && (submissionId || titleId) && (
            <SubmissionImage
              src={data.logoUrl}
              submissionId={submissionId}
              titleId={titleId}
              alt={data.title}
              className="h-48 w-auto max-w-full object-contain pb-8 mr-auto"
            />
          )}
          {data.logoUrl && (!isLogoR2 || (!submissionId && !titleId)) && (
            <img src={data.logoUrl} alt={data.title} className="h-48 w-auto max-w-full object-contain pb-8 mr-auto" />
          )}
          {!data.logoUrl && <h1 className="text-7xl pb-8 pr-8 font-bold leading-tight">{data.title}</h1>}

          {/* Enhanced metadata row - matches TitleHero format */}
          <div className="flex items-center pb-1 gap-1 text-sm text-neutral-200">
            {data.year && <span>{data.year}</span>}
            {data.year && data.runtime && <span className="mx-2">•</span>}
            {data.runtime && <span>{data.runtime} min</span>}
            {(data.year || data.runtime) && data.resolution && <span className="mx-2">•</span>}
            {data.resolution && (
              <span
                className={`${SPACING.badges.padding} bg-neutral-800 border border-neutral-700 rounded text-sm font-semibold tracking-wide`}
              >
                {data.resolution}
              </span>
            )}
            {(data.year || data.runtime || data.resolution) && languageNames.length > 0 && (
              <span className="mx-2">•</span>
            )}
            {languageNames.length > 0 && <span>{languageNames.join(", ")}</span>}
          </div>

          {/* Genres and Country - matches TitleHero */}
          {((data.genres && data.genres.length > 0) || data.country) && (
            <div className="flex flex-wrap pb-1 gap-2 items-center">
              {data.genres &&
                data.genres.length > 0 &&
                data.genres.map((genre) => (
                  <span key={genre} className="py-1 text-neutral-200 text-sm">
                    {genre}
                  </span>
                ))}
              {data.genres && data.genres.length > 0 && data.country && <span className="mx-2">•</span>}
              {data.country && (
                <span className="py-1 text-white font-medium text-sm">{normalizeCountryName(data.country)}</span>
              )}
            </div>
          )}

          {/* Director - matches TitleHero format */}
          {data.director && (
            <p className="text-sm text-neutral-200 pb-8">
              Directed by <span className="text-white font-medium">{data.director}</span>
            </p>
          )}

          {/* Synopsis - matches TitleHero */}
          {data.synopsis && <p className="text-base text-neutral-200 pb-8 leading-relaxed">{data.synopsis}</p>}

          {/* Action Buttons - Preview (non-functional) */}
          <div className="flex gap-4 pb-0">
            {/* Play Button */}
            <Button variant="ghost-sm" onClick={undefined}>
              <Play size={20} className="mr-2" />
              <span className="text-md font-semibold">Play</span>
            </Button>

            {/* Trailer Button */}
            <Button variant="ghost-sm" onClick={undefined}>
              <Video size={20} className="mr-2" />
              <span className="text-md font-semibold">Trailer</span>
            </Button>

            {/* Watchlist Button */}
            <Button variant="ghost-sm" onClick={undefined}>
              <Info size={20} className="mr-2" color={"currentColor"} />
              <span className="text-md font-semibold">{"Add to List"}</span>
            </Button>

            {/* Like Button */}
            <Button variant="ghost-sm" onClick={undefined}>
              <ThumbsUp size={20} className="mr-2" color={"currentColor"} />
              <span className="text-md font-semibold">{"Like"}</span>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * TitlePreview - Shows visual preview of how title will appear
 * Scales content uniformly to fit within container while maintaining aspect ratio
 */
export function TitlePreview({
  data,
  variant = "compact",
  submissionId = null,
  titleId = null,
}: Readonly<TitlePreviewProps>): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Calculate scale based on container width
  // Baseline: 1920px width = scale 1.0 (full desktop size)
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      // Scale proportionally from baseline of 1920px
      // Min scale of 0.4 to prevent content from becoming too small
      const newScale = Math.max(0.4, containerWidth / 1920);
      setScale(newScale);
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <div ref={containerRef} className="bg-black rounded-lg aspect-video overflow-hidden relative">
      <div
        className="absolute inset-0 origin-top-left"
        style={{
          transform: `scale(${scale})`,
          width: `${100 / scale}%`,
          height: `${100 / scale}%`,
        }}
      >
        <PreviewHero data={data} submissionId={submissionId} titleId={titleId} />
      </div>

      {/* Full Preview - Cast/Crew sections (future enhancement) */}
      {variant === "full" && (
        <section className="py-8 px-8 bg-neutral-900">
          <p className="text-sm text-neutral-500 text-center">Full preview with cast and crew sections coming soon</p>
        </section>
      )}
    </div>
  );
}
